import React, { useEffect, useMemo, useState } from "react";
import { Pencil, X, Save, Printer } from "lucide-react";
import {
  db,
  doc,
  setDoc,
  onSnapshot
} from "../../firebase";
import "./SchedulePage.css";
import Loader from "../Loader";


const translations = {
  ru: {
    days: ["Понедельник", "Вторник", "Среда", "Четверг", "Пятница"],
    scheduleTitle: "Расписание",
    scheduleSubtitle: "Расписание занятий по направлению, курсу, семестру и учебному году",
    edit: "Редактировать",
    cancel: "Отмена",
    save: "Сохранить",
    print: "Печать",
    direction: "Направление",
    course: "Курс",
    semester: "Семестр",
    studyYear: "Учебный год",
    addYear: "Добавить год",
    editSelected: "Изменить выбранный",
    semesterNotSelected: "Семестр не выбран",
    yearNotSelected: "Год не выбран",
    noData: "Пока нет данных",
    choose: "Выберите",
    schedulePrintTitle: "Расписание занятий",
    day: "День",
    time: "Время",
    discipline: "Дисциплина",
    teacher: "Преподаватель",
    lessonType: "Тип занятия",
    room: "Ауд.",
    chooseTeacher: "Выберите преподавателя",
    lecture: "Лекция",
    practice: "Практика",
    laboratory: "Лаборатория",
    lecturePractice: "Лек/прак",
    autumnSemester: "Күзгү семестр",
    springSemester: "Жазгы семестр",
    selectAll: "Выберите направление, курс, семестр и учебный год",
    chooseBeforeSave: "Выберите направление, курс, семестр и учебный год",
    settingsSaveError: "Ошибка сохранения настроек",
    settingsLoadError: "Ошибка загрузки настроек",
    scheduleSaved: "Расписание сохранено",
    scheduleSaveError: "Ошибка сохранения расписания",
    scheduleLoaded: "Расписание загружено",
    scheduleEmpty: "Для выбранных данных расписание пока не добавлено",
    scheduleLoadError: "Ошибка загрузки расписания",
    promptYear: "Введите учебный год, например 2025-2026",
    editYearPrompt: "Изменить учебный год",
    chooseYearFirst: "Сначала выберите учебный год",
    yearExists: "Такой учебный год уже есть",
    loading: "Загрузка...",
    studentInfoNotFound: "У студента не указан курс или направление",
    numerator: "Верх",
    denominator: "Низ",
    split: "Сплит",
    unsplit: "Объединить"
  },

  kg: {
    days: ["Дүйшөмбү", "Шейшемби", "Шаршемби", "Бейшемби", "Жума"],
    scheduleTitle: "Окуу жүгүртмө",
    scheduleSubtitle: "Багыт, курс, семестр жана окуу жылы боюнча сабактардын расписаниеси",
    edit: "Өзгөртүү",
    cancel: "Жокко чыгаруу",
    save: "Сактоо",
    print: "Басып чыгаруу",
    direction: "Багыт",
    course: "Курс",
    semester: "Семестр",
    studyYear: "Окуу жылы",
    addYear: "Жыл кошуу",
    editSelected: "Тандалганды өзгөртүү",
    semesterNotSelected: "Семестр тандалган жок",
    yearNotSelected: "Жыл тандалган жок",
    noData: "Азырынча маалымат жок",
    choose: "Тандаңыз",
    schedulePrintTitle: "Сабактардын окуу жүгүртсөсү",
    day: "Күн",
    time: "Убакыт",
    discipline: "Дисциплина",
    teacher: "Окутуучу",
    lessonType: "Сабактын түрү",
    room: "Ауд.",
    chooseTeacher: "Окутуучуну тандаңыз",
    lecture: "Лекция",
    practice: "Практика",
    laboratory: "Лаборатория",
    lecturePractice: "Лек/прак",
    autumnSemester: "Күзгү семестр",
    springSemester: "Жазгы семестр",
    selectAll: "Багытты, курсту, семестрди жана окуу жылын тандаңыз",
    chooseBeforeSave: "Багытты, курсту, семестрди жана окуу жылын тандаңыз",
    settingsSaveError: "Жөндөөлөрдү сактоодо ката кетти",
    settingsLoadError: "Жөндөөлөрдү жүктөөдө ката кетти",
    scheduleSaved: "Окуу жүгүртмө сакталды",
    scheduleSaveError: "Окуу жүгүртмөнү сактоодо ката кетти",
    scheduleLoaded: "Окуу жүгүртмө жүктөлдү",
    scheduleEmpty: "Тандалган маалымат боюнча окуу жүгүртмө азырынча жок",
    scheduleLoadError: "Окуу жүгүртмөнү жүктөөдө ката кетти",
    promptYear: "Окуу жылын жазыңыз, мисалы 2025-2026",
    editYearPrompt: "Окуу жылын өзгөртүү",
    chooseYearFirst: "Алгач окуу жылын тандаңыз",
    yearExists: "Мындай окуу жылы бар",
    loading: "Жүктөлүүдө...",
    studentInfoNotFound: "Студенттин курсу же багыты көрсөтүлгөн эмес",
    numerator: "Үстү",
    denominator: "Асты",
    split: "Бөлүү",
    unsplit: "Бириктирүү"
  },

  en: {
    days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    scheduleTitle: "Schedule",
    scheduleSubtitle: "Class schedule by direction, course, semester and academic year",
    edit: "Edit",
    cancel: "Cancel",
    save: "Save",
    print: "Print",
    direction: "Direction",
    course: "Course",
    semester: "Semester",
    studyYear: "Academic year",
    addYear: "Add year",
    editSelected: "Edit selected",
    semesterNotSelected: "Semester not selected",
    yearNotSelected: "Academic year not selected",
    noData: "No data yet",
    choose: "Choose",
    schedulePrintTitle: "Class schedule",
    day: "Day",
    time: "Time",
    discipline: "Discipline",
    teacher: "Teacher",
    lessonType: "Lesson type",
    room: "Room",
    chooseTeacher: "Choose teacher",
    lecture: "Lecture",
    practice: "Practice",
    laboratory: "Laboratory",
    lecturePractice: "Lecture/practice",
    autumnSemester: "Күзгү семестр",
    springSemester: "Жазгы семестр",
    selectAll: "Choose direction, course, semester and academic year",
    chooseBeforeSave: "Choose direction, course, semester and academic year",
    settingsSaveError: "Settings save error",
    settingsLoadError: "Settings load error",
    scheduleSaved: "Schedule saved",
    scheduleSaveError: "Schedule save error",
    scheduleLoaded: "Schedule loaded",
    scheduleEmpty: "No schedule for selected data yet",
    scheduleLoadError: "Schedule load error",
    promptYear: "Enter academic year, for example 2025-2026",
    editYearPrompt: "Edit academic year",
    chooseYearFirst: "Choose academic year first",
    yearExists: "This academic year already exists",
    loading: "Loading...",
    studentInfoNotFound: "Student course or direction is not specified",
    numerator: "Top",
    denominator: "Bottom",
    split: "Split",
    unsplit: "Merge"
  }
};

const times = [
  "8.00-9.20",
  "9.30-10.50",
  "11.00-12.20",
  "12.30-13.50",
  "14.00-15.20"
];

const fixedDirections = ["ИВТ", "ПИЭ"];
const fixedCourses = ["1 курс", "2 курс", "3 курс", "4 курс"];
const fixedSemesters = ["Күзгү семестр", "Жазгы семестр"];

function normalizeDirection(value, fallback = "") {
  if (!value) return fallback;

  const str = String(value).trim().toUpperCase();

  if (str.includes("ПИЭ")) return "ПИЭ";
  if (str.includes("ИВТ")) return "ИВТ";

  return fallback;
}

function normalizeCourse(value, fallback = "") {
  if (!value) return fallback;

  const str = String(value).trim().toLowerCase();

  if (str.includes("1")) return "1 курс";
  if (str.includes("2")) return "2 курс";
  if (str.includes("3")) return "3 курс";
  if (str.includes("4")) return "4 курс";

  return fallback;
}

function normalizeRole(value = "") {
  const role = String(value || "").trim().toLowerCase();

  if (role === "admin" || role === "администратор") return "admin";

  if (
    role === "teacher" ||
    role === "преподаватель" ||
    role === "мугалим" ||
    role === "окутуучу"
  ) {
    return "teacher";
  }

  if (
    role === "lab" ||
    role === "laborant" ||
    role === "лаборант"
  ) {
    return "lab";
  }

  if (role === "student" || role === "студент") return "student";

  return role || "admin";
}

function getTeacherName(teacher = {}) {
  return (
    teacher.fullName ||
    teacher.name ||
    teacher.displayName ||
    teacher.email ||
    ""
  );
}

function createEmptySchedule(daysList) {
  const rows = [];

  daysList.forEach((day) => {
    times.forEach((time, index) => {
      rows.push({
        day,
        time,
        discipline: "",
        teacherId: "",
        teacherName: "",
        lessonType: "",
        room: "",
        isSplit: false,
        disciplineSub: "",
        teacherIdSub: "",
        teacherNameSub: "",
        lessonTypeSub: "",
        roomSub: "",
        isFirstRowOfDay: index === 0
      });
    });
  });

  return rows;
}

function normalizeRowsDays(rows, daysList) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return createEmptySchedule(daysList);
  }

  const emptyRows = createEmptySchedule(daysList);

  return emptyRows.map((emptyRow, index) => {
    const row = rows[index] || {};

    return {
      ...emptyRow,
      ...row,
      day: emptyRow.day,
      time: row.time || emptyRow.time,
      discipline: row.discipline || "",
      teacherId: row.teacherId || "",
      teacherName: row.teacherName || "",
      lessonType: row.lessonType || "",
      room: row.room || "",
      isSplit: row.isSplit || false,
      disciplineSub: row.disciplineSub || "",
      teacherIdSub: row.teacherIdSub || "",
      teacherNameSub: row.teacherNameSub || "",
      lessonTypeSub: row.lessonTypeSub || "",
      roomSub: row.roomSub || "",
      isFirstRowOfDay: emptyRow.isFirstRowOfDay
    };
  });
}

function makeScheduleDocId({ direction, course, studyYear, semester }) {
  return [direction, course, studyYear, semester]
    .map((item) =>
      String(item || "")
        .trim()
        .replaceAll("/", "-")
        .replaceAll("\\", "-")
        .replace(/\s+/g, "_")
    )
    .join("__");
}

function EditableSelect({
  label,
  value,
  options,
  onChange,
  onAdd,
  onEdit,
  addText,
  editText,
  canManage = false,
  canSelect = true,
  placeholder = "Выберите",
  emptyText = "Пока нет данных"
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="editable-select">
      <label className="editable-select-label">{label}</label>

      <button
        type="button"
        className="editable-select-main"
        disabled={!canSelect}
        onClick={() => canSelect && setOpen((prev) => !prev)}
      >
        <span>{value || placeholder}</span>
        <span className="editable-select-arrow">▾</span>
      </button>

      {open && (
        <div className="editable-select-menu">
          {options.length === 0 && (
            <div className="editable-select-empty">{emptyText}</div>
          )}

          {options.map((item) => (
            <button
              type="button"
              key={item}
              className={`editable-select-option ${
                item === value ? "active" : ""
              }`}
              onClick={() => {
                onChange(item);
                setOpen(false);
              }}
            >
              {item}
            </button>
          ))}

          {canManage && (
            <>
              <div className="editable-select-divider" />

              <button
                type="button"
                className="editable-select-action"
                onClick={() => {
                  setOpen(false);
                  onAdd();
                }}
              >
                + {addText}
              </button>

              <button
                type="button"
                className="editable-select-action"
                onClick={() => {
                  setOpen(false);
                  onEdit();
                }}
              >
                {editText}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function SchedulePage({
  mode = "admin",
  role = mode,
  teachers = [],
  currentStudent = null,
  currentTeacher = null,
  language = "ru",
  t: parentT = {},
  readOnly = false,
  readonly = false
}) {
  const text = {
    ...(translations[language] || translations.ru),
    ...parentT
  };

  const days = text.days || translations.ru.days;

  const normalizedMode = normalizeRole(mode);
  const normalizedRole = normalizeRole(role || mode);

  const isAdmin = normalizedMode === "admin" || normalizedRole === "admin";
  const isTeacher = normalizedMode === "teacher" || normalizedRole === "teacher";
  const isLab = normalizedMode === "lab" || normalizedRole === "lab";
  const isStudent = normalizedMode === "student" || normalizedRole === "student";

  const isReadOnly = Boolean(readOnly || readonly);

  const studentDirection = normalizeDirection(
    currentStudent?.direction ||
      currentStudent?.department ||
      currentStudent?.speciality,
    ""
  );

  const studentCourse = normalizeCourse(currentStudent?.course, "");

  const studentStudyYear =
    currentStudent?.studyYear ||
    currentStudent?.academicYear ||
    localStorage.getItem("academicYear") ||
    "";

  const studentSemester =
    currentStudent?.semester ||
    currentStudent?.currentSemester ||
    "Күзгү семестр";

  const [direction, setDirection] = useState(() => {
    if (isStudent) return studentDirection;
    return "ИВТ";
  });

  const [course, setCourse] = useState(() => {
    if (isStudent) return studentCourse;
    return "1 курс";
  });

  const [studyYears, setStudyYears] = useState([]);

  const [semester, setSemester] = useState(() => {
    if (isStudent) return studentSemester;
    return "Күзгү семестр";
  });

  const [studyYear, setStudyYear] = useState(() => {
    if (isStudent) return studentStudyYear;
    return localStorage.getItem("academicYear") || "";
  });

  const [rows, setRows] = useState(() => createEmptySchedule(days));
  const [isEdit, setIsEdit] = useState(false);
  const [status, setStatus] = useState(text.selectAll);
  const [settingsReady, setSettingsReady] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const defaultRows = useMemo(() => createEmptySchedule(days), [days]);

  const allDisciplines = useMemo(() => {
    const list = teachers.flatMap((t) => t.disciplines || []);
    return [...new Set(list)].filter(Boolean);
  }, [teachers]);

  const canShowFilters = !isStudent;

  /*
    access logic:
    none -> sidebar бул компонентти көрсөтпөйт
    read -> readOnly=true, таблица көрүнөт, бирок өзгөртүү жок
    edit -> readOnly=false, өзгөртүү/сактоо иштейт
  */
  const canManageSchedule = !isStudent && !isReadOnly;
  const canEditSchedule = canManageSchedule && isEdit;

  function getScheduleDocId() {
    if (!direction || !course || !studyYear || !semester) {
      return null;
    }

    return makeScheduleDocId({
      direction,
      course,
      studyYear,
      semester
    });
  }

  async function saveSettings(nextSettings) {
    if (!canManageSchedule) return;

    try {
      const nextYears = Array.isArray(nextSettings.studyYears)
        ? nextSettings.studyYears
        : [];

      await setDoc(
        doc(db, "scheduleSettings", "main"),
        {
          semesters: fixedSemesters,
          studyYears: nextYears,
          updatedAt: new Date().toISOString()
        },
        { merge: true }
      );

      setStudyYears(nextYears);
    } catch (error) {
      console.error(error);
      alert(text.settingsSaveError);
    }
  }

  async function saveSchedule() {
    if (!canManageSchedule) return;

    const scheduleDocId = getScheduleDocId();

    if (!scheduleDocId) {
      alert(text.chooseBeforeSave);
      return;
    }

    const payload = {
      direction,
      course,
      semester,
      studyYear,
      rows: normalizeRowsDays(rows, days),
      updatedAt: new Date().toISOString(),
      updatedByRole: normalizedRole,
      updatedByName:
        currentTeacher?.fullName ||
        currentTeacher?.name ||
        currentTeacher?.email ||
        ""
    };

    try {
      await setDoc(doc(db, "schedules", scheduleDocId), payload, {
        merge: true
      });

      setIsEdit(false);
      setStatus(text.scheduleSaved);
    } catch (error) {
      console.error(error);
      setStatus(text.scheduleSaveError);
    }
  }

  async function addStudyYear() {
    if (!canManageSchedule) return;

    const value = prompt(text.promptYear);

    if (!value) return;

    const newYear = value.trim();

    if (!newYear) return;

    if (studyYears.includes(newYear)) {
      alert(text.yearExists);
      return;
    }

    const nextYears = [...studyYears, newYear];

    await saveSettings({
      studyYears: nextYears
    });

    setStudyYear(newYear);
    localStorage.setItem("academicYear", newYear);
  }

  async function editStudyYear() {
    if (!canManageSchedule) return;

    if (!studyYear) {
      alert(text.chooseYearFirst);
      return;
    }

    const value = prompt(text.editYearPrompt, studyYear);

    if (!value) return;

    const newYear = value.trim();

    if (!newYear) return;

    if (studyYears.includes(newYear) && newYear !== studyYear) {
      alert(text.yearExists);
      return;
    }

    const nextYears = studyYears.map((item) =>
      item === studyYear ? newYear : item
    );

    await saveSettings({
      studyYears: nextYears
    });

    setStudyYear(newYear);
    localStorage.setItem("academicYear", newYear);
  }

  function getFieldName(baseField, subType) {
    if (subType === "bottom") {
      return `${baseField}Sub`;
    }
    return baseField;
  }

  function toggleRowSplit(index) {
    if (!canEditSchedule) return;

    setRows((prevRows) => {
      const updatedRows = [...prevRows];
      const row = updatedRows[index];
      const isNowSplit = !row.isSplit;

      updatedRows[index] = {
        ...row,
        isSplit: isNowSplit,
        ...(isNowSplit ? {} : {
          disciplineSub: "",
          teacherIdSub: "",
          teacherNameSub: "",
          lessonTypeSub: "",
          roomSub: ""
        })
      };
      return updatedRows;
    });
  }

  function updateCell(index, baseField, value, subType = "single") {
    if (!canEditSchedule) return;

    const field = getFieldName(baseField, subType);

    setRows((prevRows) => {
      const updatedRows = [...prevRows];
      const updatedRow = {
        ...updatedRows[index],
        [field]: value
      };

      if (baseField === "discipline") {
        const cleanDisc = String(value || "").trim().toLowerCase();
        if (cleanDisc) {
          const matchedTeachers = teachers.filter((t) =>
            (t.disciplines || [])
              .map((d) => String(d || "").trim().toLowerCase())
              .includes(cleanDisc)
          );
          if (matchedTeachers.length === 1) {
            const selectedTeacher = matchedTeachers[0];
            const tIdField = getFieldName("teacherId", subType);
            const tNameField = getFieldName("teacherName", subType);
            updatedRow[tIdField] = selectedTeacher.id;
            updatedRow[tNameField] = getTeacherName(selectedTeacher);
          }
        }
      }

      updatedRows[index] = updatedRow;
      return updatedRows;
    });
  }

  function updateTeacher(index, teacherId, subType = "single") {
    if (!canEditSchedule) return;

    const selectedTeacher = teachers.find(
      (teacher) => String(teacher.id) === String(teacherId)
    );

    const tIdField = getFieldName("teacherId", subType);
    const tNameField = getFieldName("teacherName", subType);
    const discField = getFieldName("discipline", subType);

    setRows((prevRows) => {
      const updatedRows = [...prevRows];
      const updatedRow = {
        ...updatedRows[index],
        [tIdField]: teacherId,
        [tNameField]: selectedTeacher ? getTeacherName(selectedTeacher) : ""
      };

      if (selectedTeacher && Array.isArray(selectedTeacher.disciplines) && selectedTeacher.disciplines.length === 1) {
        updatedRow[discField] = selectedTeacher.disciplines[0];
      }

      updatedRows[index] = updatedRow;
      return updatedRows;
    });
  }

  function printSchedule() {
    window.print();
  }

  function handleCancelEdit() {
    setIsEdit(false);
  }

  function renderDisciplineCell(row, index, subType = "single") {
    const discValue = row[getFieldName("discipline", subType)] || "";
    const teacherIdVal = row[getFieldName("teacherId", subType)] || "";

    if (canEditSchedule) {
      const datalistId = `schedule-dl-${index}-${subType}`;
      let suggestions = [];
      if (teacherIdVal) {
        const tObj = teachers.find((t) => String(t.id) === String(teacherIdVal));
        suggestions = tObj ? tObj.disciplines || [] : [];
      } else {
        suggestions = allDisciplines;
      }

      return (
        <div className="schedule-input-wrapper-split">
          {(subType === "top" || subType === "bottom") && (
            <span className={`week-badge ${subType}-badge`}>
              {subType === "top" ? text.numerator : text.denominator}
            </span>
          )}
          <input
            value={discValue}
            onChange={(event) =>
              updateCell(index, "discipline", event.target.value, subType)
            }
            placeholder={text.discipline}
            list={datalistId}
          />
          <datalist id={datalistId}>
            {suggestions.map((s, idx) => (
              <option key={idx} value={s} />
            ))}
          </datalist>
        </div>
      );
    }

    return (
      <div className="schedule-readonly-cell split-view-cell">
        {(subType === "top" || subType === "bottom") && (
          <span className={`week-badge ${subType}-badge`}>
            {subType === "top" ? text.numerator : text.denominator}
          </span>
        )}
        <span className="discipline-text">{discValue}</span>
      </div>
    );
  }

  function renderTeacherCell(row, index, subType = "single") {
    const teacherIdVal = row[getFieldName("teacherId", subType)] || "";
    const teacherNameVal = row[getFieldName("teacherName", subType)] || "";

    if (canEditSchedule) {
      return (
        <select
          className="teacher-select"
          value={teacherIdVal}
          onChange={(event) => updateTeacher(index, event.target.value, subType)}
        >
          <option value="">{text.chooseTeacher}</option>

          {teachers.map((teacher) => (
            <option key={teacher.id} value={teacher.id}>
              {getTeacherName(teacher)}
            </option>
          ))}
        </select>
      );
    }

    return <div className="schedule-readonly-cell">{teacherNameVal}</div>;
  }

  function renderLessonTypeCell(row, index, subType = "single") {
    const lessonTypeVal = row[getFieldName("lessonType", subType)] || "";

    if (canEditSchedule) {
      return (
        <select
          className="type-select"
          value={lessonTypeVal}
          onChange={(event) =>
            updateCell(index, "lessonType", event.target.value, subType)
          }
        >
          <option value="">{text.choose}</option>
          <option value={text.lecture}>{text.lecture}</option>
          <option value={text.practice}>{text.practice}</option>
          <option value={text.laboratory}>{text.laboratory}</option>
          <option value={text.lecturePractice}>{text.lecturePractice}</option>
        </select>
      );
    }

    return <div className="schedule-readonly-cell">{lessonTypeVal}</div>;
  }

  function renderRoomCell(row, index, subType = "single") {
    const roomVal = row[getFieldName("room", subType)] || "";

    if (canEditSchedule) {
      return (
        <input
          value={roomVal}
          onChange={(event) => updateCell(index, "room", event.target.value, subType)}
          placeholder={text.room}
        />
      );
    }

    return <div className="schedule-readonly-cell">{roomVal}</div>;
  }

  useEffect(() => {
    setRows((prevRows) => normalizeRowsDays(prevRows, days));
  }, [days]);

  useEffect(() => {
    if (!isStudent) return;

    setDirection(studentDirection);
    setCourse(studentCourse);
    setSemester(studentSemester || "Күзгү семестр");

    if (studentStudyYear) {
      setStudyYear(studentStudyYear);
    }
  }, [
    isStudent,
    studentDirection,
    studentCourse,
    studentSemester,
    studentStudyYear
  ]);

  useEffect(() => {
    if (!canManageSchedule && isEdit) {
      setIsEdit(false);
    }
  }, [canManageSchedule, isEdit]);

  useEffect(() => {
    const settingsDocRef = doc(db, "scheduleSettings", "main");

    const unsubscribe = onSnapshot(
      settingsDocRef,
      async (snapshot) => {
        try {
          if (snapshot.exists()) {
            const data = snapshot.data() || {};
            const loadedYears = Array.isArray(data.studyYears)
              ? data.studyYears
              : [];

            setStudyYears(loadedYears);

            if (isStudent) {
              if (studentStudyYear) {
                setStudyYear(studentStudyYear);
              } else if (loadedYears.length > 0) {
                setStudyYear(loadedYears[0]);
              } else {
                setStudyYear("");
              }
            } else {
              const savedYear = localStorage.getItem("academicYear");

              if (loadedYears.length > 0) {
                if (savedYear && loadedYears.includes(savedYear)) {
                  setStudyYear(savedYear);
                } else {
                  setStudyYear(loadedYears[0]);
                  localStorage.setItem("academicYear", loadedYears[0]);
                }
              } else {
                setStudyYear("");
              }
            }

            if (!fixedSemesters.includes(semester)) {
              setSemester("Күзгү семестр");
            }
          } else {
            if (canManageSchedule) {
              await setDoc(settingsDocRef, {
                semesters: fixedSemesters,
                studyYears: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              });
            }

            setStudyYears([]);
            setStudyYear("");
            setSemester("Күзгү семестр");
          }

          setSettingsReady(true);
        } catch (error) {
          console.error(error);
          setSettingsReady(true);
          setStatus(text.settingsLoadError);
        }
      },
      (error) => {
        console.error(error);
        setSettingsReady(true);
        setStatus(text.settingsLoadError);
      }
    );

    return () => unsubscribe();
  }, [
    canManageSchedule,
    isStudent,
    language,
    studentStudyYear,
    semester,
    text.settingsLoadError
  ]);

  useEffect(() => {
    if (!settingsReady) return;

    const scheduleDocId = getScheduleDocId();

    if (isStudent && (!studentDirection || !studentCourse)) {
      setRows(defaultRows);
      setStatus(text.studentInfoNotFound);
      return;
    }

    if (!scheduleDocId) {
      setRows(defaultRows);
      setStatus(text.selectAll);
      return;
    }

    setScheduleLoading(true);

    const scheduleDocRef = doc(db, "schedules", scheduleDocId);

    const unsubscribe = onSnapshot(
      scheduleDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() || {};
          const loadedRows = normalizeRowsDays(
            data.rows || createEmptySchedule(days),
            days
          );

          setRows(loadedRows);
          setStatus(text.scheduleLoaded);
        } else {
          setRows(createEmptySchedule(days));
          setStatus(text.scheduleEmpty);
        }

        setIsEdit(false);
        setScheduleLoading(false);
      },
      (error) => {
        console.error(error);
        setRows(createEmptySchedule(days));
        setStatus(text.scheduleLoadError);
        setScheduleLoading(false);
      }
    );

    return () => unsubscribe();
  }, [
    settingsReady,
    direction,
    course,
    studyYear,
    semester,
    language,
    isStudent,
    studentDirection,
    studentCourse,
    defaultRows,
    days,
    text.studentInfoNotFound,
    text.selectAll,
    text.scheduleLoaded,
    text.scheduleEmpty,
    text.scheduleLoadError
  ]);

  const subtitleText = isStudent
    ? `${direction || "-"} | ${course || "-"}`
    : `${direction || "-"} | ${course || "-"} | ${semester || "-"} | ${
        studyYear || "-"
      }`;

  return (
    <div
      className={`schedule-page ${
        isStudent ? "schedule-student-view" : ""
      } ${isTeacher ? "schedule-teacher-view" : ""} ${
        isLab ? "schedule-lab-view" : ""
      } ${isReadOnly ? "schedule-readonly-view" : "schedule-edit-access-view"}`}
    >
      <div className="schedule-card">
        <div className="schedule-top no-print">
          <div>
            <h1>{text.scheduleTitle}</h1>
            <p>{text.scheduleSubtitle}</p>
          </div>

          <div className="schedule-actions">
            {canManageSchedule && (
              <>
                {!isEdit ? (
                  <button
                    type="button"
                    className="schedule-btn dark"
                    onClick={() => setIsEdit(true)}
                  >
                    <Pencil size={14} style={{ marginRight: '6px' }} />
                    {text.edit}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="schedule-btn light"
                    onClick={handleCancelEdit}
                  >
                    <X size={14} style={{ marginRight: '6px' }} />
                    {text.cancel}
                  </button>
                )}

                {isEdit && (
                  <button
                    type="button"
                    className="schedule-btn dark"
                    onClick={saveSchedule}
                  >
                    <Save size={14} style={{ marginRight: '6px' }} />
                    {text.save}
                  </button>
                )}
              </>
            )}

            <button
              type="button"
              className="schedule-btn light"
              onClick={printSchedule}
            >
              <Printer size={14} style={{ marginRight: '6px' }} />
              {text.print}
            </button>
          </div>
        </div>

        {canShowFilters && (
          <div className="schedule-filters no-print">
            <div className="filter-select">
              <label>{text.direction}</label>

              <select
                value={direction}
                onChange={(event) => setDirection(event.target.value)}
              >
                {fixedDirections.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-select">
              <label>{text.course}</label>

              <select
                value={course}
                onChange={(event) => setCourse(event.target.value)}
              >
                {fixedCourses.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <EditableSelect
              label={text.semester}
              value={semester}
              options={fixedSemesters}
              onChange={setSemester}
              onAdd={() => {}}
              onEdit={() => {}}
              addText=""
              editText=""
              canManage={false}
              canSelect={true}
              placeholder={text.semesterNotSelected}
              emptyText={text.noData}
            />

            <EditableSelect
              label={text.studyYear}
              value={studyYear}
              options={studyYears}
              onChange={(year) => {
                setStudyYear(year);
                localStorage.setItem("academicYear", year);
              }}
              onAdd={addStudyYear}
              onEdit={editStudyYear}
              addText={text.addYear}
              editText={text.editSelected}
              canManage={canManageSchedule}
              canSelect={true}
              placeholder={text.yearNotSelected}
              emptyText={text.noData}
            />
          </div>
        )}

        <div className="schedule-title-print">
          <h2>{text.schedulePrintTitle}</h2>
          <p>{subtitleText}</p>
        </div>

        {scheduleLoading && (
          <div className="schedule-status no-print" style={{ display: 'flex', justifyContent: 'center', border: 'none', background: 'transparent' }}>
            <Loader text={text.loading} size={40} />
          </div>
        )}


        {!scheduleLoading && status && !isStudent && (
          <div className="schedule-status no-print">{status}</div>
        )}

        {!scheduleLoading && status && isStudent && (!direction || !course) && (
          <div className="schedule-status no-print">{status}</div>
        )}

        <div className="schedule-table-wrapper">
          <table className="schedule-main-table">
            <thead>
              <tr>
                <th className="day-col">{text.day}</th>
                <th className="time-col">{text.time}</th>
                <th>{text.discipline}</th>
                <th>{text.teacher}</th>
                <th className="type-col">{text.lessonType}</th>
                <th className="room-col">{text.room}</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => {
                const dayRows = rows.filter((r) => r.day === row.day);
                const dayRowSpan = dayRows.length + dayRows.filter((r) => r.isSplit).length;

                if (row.isSplit) {
                  return (
                    <React.Fragment key={`${row.day}-${row.time}-${index}`}>
                      <tr className="schedule-split-top-tr">
                        {row.isFirstRowOfDay && (
                          <td className="day-cell" rowSpan={dayRowSpan}>
                            <span>{row.day}</span>
                          </td>
                        )}
                        <td className="time-cell" rowSpan={2}>
                          <div className="time-cell-content">
                            <span>{row.time}</span>
                            {canEditSchedule && (
                              <button
                                type="button"
                                className="schedule-split-btn merge-btn"
                                onClick={() => toggleRowSplit(index)}
                                title={text.unsplit}
                              >
                                {text.unsplit}
                              </button>
                            )}
                          </div>
                        </td>
                        <td>{renderDisciplineCell(row, index, "top")}</td>
                        <td>{renderTeacherCell(row, index, "top")}</td>
                        <td>{renderLessonTypeCell(row, index, "top")}</td>
                        <td>{renderRoomCell(row, index, "top")}</td>
                      </tr>
                      <tr className="schedule-split-bottom-tr">
                        <td>{renderDisciplineCell(row, index, "bottom")}</td>
                        <td>{renderTeacherCell(row, index, "bottom")}</td>
                        <td>{renderLessonTypeCell(row, index, "bottom")}</td>
                        <td>{renderRoomCell(row, index, "bottom")}</td>
                      </tr>
                    </React.Fragment>
                  );
                }

                return (
                  <tr key={`${row.day}-${row.time}-${index}`}>
                    {row.isFirstRowOfDay && (
                      <td className="day-cell" rowSpan={dayRowSpan}>
                        <span>{row.day}</span>
                      </td>
                    )}
                    <td className="time-cell">
                      <div className="time-cell-content">
                        <span>{row.time}</span>
                        {canEditSchedule && (
                          <button
                            type="button"
                            className="schedule-split-btn split-btn"
                            onClick={() => toggleRowSplit(index)}
                            title={text.split}
                          >
                            {text.split}
                          </button>
                        )}
                      </div>
                    </td>
                    <td>{renderDisciplineCell(row, index, "single")}</td>
                    <td>{renderTeacherCell(row, index, "single")}</td>
                    <td>{renderLessonTypeCell(row, index, "single")}</td>
                    <td>{renderRoomCell(row, index, "single")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="schedule-bottom-space" />
      </div>
    </div>
  );
}